import styled from '@emotion/styled'
import { getLinkFieldUrl } from '../../data/prismic-helper'
import Video from './backgrounds/Video'
import { FullscreenImage } from './backgrounds/images/FullscreenImage'
import { StepsScrollProgress } from './ScrollOverMedia'
import { ImageField } from '@prismicio/client'
import { Lottie } from './backgrounds/Lottie'
import { ParallaxImage } from './backgrounds/images/ParallaxImage'
import { useInView } from 'react-intersection-observer'
import { StepScrollProgress } from '../../util/scrollPercentage'
import { useIsPortrait } from '../../util/breakpoints'
import ScrollVideo from './backgrounds/scrollVideo/ScrollVideo'
import { Stickies } from './backgrounds/Stickies'
import { ScrollOverMediaDocument } from '../../../types.generated'
import { useMemo } from 'react'

const getAltText = (
  link: ImageField<'portrait'>,
  usePortrait: boolean
): string => {
  if (usePortrait && link.portrait?.alt) {
    return link.portrait.alt
  }

  return link.alt ?? ''
}

export interface BackgroundProps {
  doc: ScrollOverMediaDocument
  activeStep: number
  stepsScrollProgress: StepsScrollProgress
  sectionIndex: number
  lang: string
}
export default function ScrollOverMediaBackground(props: BackgroundProps) {
  const isPortrait = useIsPortrait()
  const { ref, inView: mainContainerInView } = useInView({
    threshold: 0.001,
  })

  const steps = props.doc.data.body

  const backgrounds = steps.map((step, backgroundIndex) => {
    // calculate descending z-Indexes to create layers of backgrounds -> first background with highest zIndex
    const currentBgZindex = steps.length - backgroundIndex

    const backgroundCurrentlyVisible =
      mainContainerInView &&
      (props.doc.data.combine_slices || backgroundIndex === props.activeStep)

    const backgroundIsNeighbourToVisibleOne =
      backgroundIndex === props.activeStep - 1 ||
      backgroundIndex === props.activeStep + 1

    let currentBgOpacity: number
    if (props.doc.data.combine_slices) {
      // display all layers in parallax, all visible
      currentBgOpacity = 1
    } else {
      // to hide a layer once we are through, just remove it's opacity.
      // This technique removes flickers, because the next layer was already rendered in the bg.
      currentBgOpacity = backgroundIndex >= props.activeStep ? 1 : 0
    }

    const scrollPercentage = StepScrollProgress.getScrollProgressForStep(
      props.stepsScrollProgress,
      backgroundIndex
    )

    if (step.slice_type === 'video_step') {
      const videoLandscape = getLinkFieldUrl(step.primary.video_landscape)
      const videoPortrait = getLinkFieldUrl(step.primary.video_portrait)

      return (
        <BackgroundLayer
          key={backgroundIndex}
          zIndex={currentBgZindex}
          opacity={currentBgOpacity}
        >
          {step.primary.playback === 'scroll' ? (
            <ScrollVideo
              videoLandscape={videoLandscape}
              videoPortrait={videoPortrait}
              scrollPercentage={scrollPercentage}
              isActive={backgroundCurrentlyVisible}
              alt={step.primary.alt_text ?? ''}
              videoId={`${props.sectionIndex}-${backgroundIndex}`}
            />
          ) : (
            <Video
              videoLandscape={videoLandscape}
              videoPortrait={videoPortrait}
              playing={backgroundCurrentlyVisible}
              scrollPercentage={scrollPercentage}
              alt={step.primary.alt_text ?? ''}
              videoId={`${props.sectionIndex}-${backgroundIndex}`}
              type={step.primary.playback}
              lang={props.lang}
            />
          )}
        </BackgroundLayer>
      )
    }

    if (step.slice_type === 'image_step') {
      const altText = getAltText(step.primary.image, isPortrait)

      return (
        <BackgroundLayer
          key={backgroundIndex}
          zIndex={currentBgZindex}
          opacity={currentBgOpacity}
        >
          <FullscreenImage
            image={step.primary.image}
            alt={altText}
            isActive={backgroundCurrentlyVisible}
            scrollPercentage={scrollPercentage}
            style={{
              backgroundColor: step.primary.background_color ?? 'transparent',
            }}
            effectType={step.primary.effect_type}
            effectPlayback={step.primary.effect_playback}
            effectStrength={step.primary.effect_strength}
          />
        </BackgroundLayer>
      )
    }

    if (step.slice_type === 'image_parallax_step') {
      return (
        <BackgroundLayer
          key={backgroundIndex}
          zIndex={currentBgZindex}
          opacity={currentBgOpacity}
        >
          <ParallaxImage
            imageLandscape={step.primary.image_landscape}
            imagePortrait={step.primary.image_portrait}
            style={{
              backgroundColor: step.primary.background_color ?? 'transparent',
            }}
            scrollPercentage={scrollPercentage}
          />
        </BackgroundLayer>
      )
    }

    if (step.slice_type === 'lottie_step') {
      const lottieLandscape = getLinkFieldUrl(step.primary.lottie_landscape)
      const lottiePortrait = getLinkFieldUrl(step.primary.lottie_portrait)

      return (
        <BackgroundLayer
          key={backgroundIndex}
          zIndex={currentBgZindex}
          opacity={currentBgOpacity}
        >
          <Lottie
            playback={step.primary.playback}
            scrollPercentage={scrollPercentage}
            lottieLandscape={lottieLandscape}
            lottiePortrait={lottiePortrait}
            shouldLoadAnimation={
              backgroundCurrentlyVisible || backgroundIsNeighbourToVisibleOne
            }
          />
        </BackgroundLayer>
      )
    }
  })

  const stickies = useMemo(() => {
    return <Stickies data={props.doc.data.stickies} zIndex={steps.length + 2} />
  }, [props.doc.data.stickies, steps])

  const hasDimmingLayer =
    props.doc.data.dimming_layer_opacity &&
    props.doc.data.dimming_layer_opacity > 0 &&
    props.doc.data.dimming_layer_color

  return (
    <BackgroundContainer ref={ref}>
      {stickies}
      {hasDimmingLayer && (
        <DimmingLayer
          zIndex={steps.length + 1}
          opacity={props.doc.data.dimming_layer_opacity ?? 0}
          backgroundColor={
            props.doc.data.dimming_layer_color ?? 'rgb(255,255,255)'
          }
        />
      )}
      {backgrounds}
    </BackgroundContainer>
  )
}

interface BackgroundLayerProps {
  zIndex: number
  opacity: number
}

const BackgroundLayer = styled.div<BackgroundLayerProps>`
  position: absolute;
  overflow: hidden;
  height: 100vh;
  width: 100%;
  pointer-events: none;

  z-index: ${(props) => props.zIndex};
  transition: opacity 0.5s ease-in-out;
  opacity: ${(props) => props.opacity};
`

interface DimmingLayerProps {
  zIndex: number
  opacity: number
  backgroundColor: string
}

const DimmingLayer = styled.div<DimmingLayerProps>`
  position: absolute;
  overflow: hidden;
  width: 100%;
  height: 100vh;
  pointer-events: none;
  background-color: ${(props) => props.backgroundColor};
  opacity: ${(props) => props.opacity / 100};
  z-index: ${(props) => props.zIndex};
`

const BackgroundContainer = styled.div`
  position: sticky;
  top: 0px;
  height: 100vh;
  width: 100%;

  display: flex;
  flex-direction: row;
  justify-content: center;
`
